import { useRef, useMemo, useState } from 'react'
import { OrbitControls, Stars, Float, Text } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import useAppStore from '../stores/appStore'
import Lights from './Lights'
import PoemDisplay from './PoemDisplay'
import PoemNode from './PoemNode'
import CameraController from './CameraController'
import AudioManager from './AudioManager'
import { chapters } from '../data/poems'

// Import all chapter geometries
import {
  NestedSpheres,
  VesicaPiscis,
  FlowerOfLife,
  PlasmaAurora,
  Caduceus,
  MovementFigure,
  CymaticsPlate,
  BreathingSphere,
  NeuralNetwork,
  HeartTorus,
  CollectiveNetwork,
  AscendingSpiral,
  IntegratedFractal,
  SteppingStones
} from './ChapterGeometry'

// Import orbit behaviors
import { useOrbitBehavior } from './PoemOrbitBehaviors'

// Map chapter IDs to geometry components
const chapterGeometries = [
  NestedSpheres,      // Ch 1: Multidimensional Human
  VesicaPiscis,       // Ch 2: Language of Coherence
  FlowerOfLife,       // Ch 3: Sacred Geometry
  PlasmaAurora,       // Ch 4: Plasma Consciousness
  Caduceus,           // Ch 5: Hermetic Principles
  MovementFigure,     // Ch 6: Movement Medicine
  CymaticsPlate,      // Ch 7: Silent Vibrations
  BreathingSphere,    // Ch 8: Breath as Bridge
  NeuralNetwork,      // Ch 9: Nervous System
  HeartTorus,         // Ch 10: Love as Root
  CollectiveNetwork,  // Ch 11: Collective Consciousness
  AscendingSpiral,    // Ch 12: Transcendence
  IntegratedFractal,  // Ch 13: The Normal Map
  SteppingStones      // Ch 14: Practical Protocols
]

// Chapter visualization with orbiting poem nodes
function ChapterVisualization({ chapterIndex, isActive, audioData }) {
  const groupRef = useRef()
  const GeometryComponent = chapterGeometries[chapterIndex]
  const chapter = chapters[chapterIndex]

  // Get orbit positions for 9 poem nodes
  const orbitPositions = useOrbitBehavior(chapterIndex + 1, audioData)

  // Animate group
  useFrame((state, delta) => {
    if (groupRef.current && isActive) {
      groupRef.current.rotation.y += delta * 0.1
    }
  })

  // Fallback if geometry component is missing
  if (!GeometryComponent) {
    console.warn(`Missing geometry component for chapter ${chapterIndex}`)
    return (
      <group ref={groupRef}>
        <mesh>
          <sphereGeometry args={[1, 32, 32]} />
          <meshStandardMaterial color={chapter?.color || '#D4AF37'} />
        </mesh>
      </group>
    )
  }

  return (
    <group ref={groupRef}>
      {/* Main chapter geometry */}
      <GeometryComponent
        audioData={audioData}
        isActive={isActive}
        position={[0, 0, 0]}
      />

      {/* 9 orbiting poem nodes */}
      {orbitPositions.map((pos, i) => (
        <PoemNode
          key={i}
          position={[pos.x, pos.y, pos.z]}
          poemIndex={i}
          chapterIndex={chapterIndex}
          color={chapter?.color || '#D4AF37'}
          audioData={audioData}
          isActive={isActive}
        />
      ))}
    </group>
  )
}

// Overview mode showing all 14 chapters in golden spiral
function ChapterOverview({ audioData, onSelectChapter }) {
  const groupRef = useRef()
  const [hoveredIndex, setHoveredIndex] = useState(null)

  // Golden ratio for spiral placement
  const phi = (1 + Math.sqrt(5)) / 2

  // Calculate positions for 14 chapters in golden spiral
  const chapterPositions = useMemo(() => {
    return chapters.map((_, i) => {
      const angle = i * (Math.PI * 2) / phi
      const radius = 3 + i * 0.5
      return {
        x: Math.cos(angle) * radius,
        y: Math.sin(i * 0.3) * 2,
        z: Math.sin(angle) * radius
      }
    })
  }, [])

  useFrame((state, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.05
    }
  })

  return (
    <group ref={groupRef}>
      {chapters.map((chapter, i) => {
        const pos = chapterPositions[i]
        const GeometryComponent = chapterGeometries[i]

        const isHovered = hoveredIndex === i

        return (
          <group
            key={i}
            position={[pos.x, pos.y, pos.z]}
            scale={isHovered ? 0.5 : 0.4}
            onClick={(e) => {
              e.stopPropagation()
              onSelectChapter(i)
            }}
            onPointerOver={(e) => {
              e.stopPropagation()
              setHoveredIndex(i)
            }}
            onPointerOut={(e) => {
              e.stopPropagation()
              setHoveredIndex(null)
            }}
          >
            <Float speed={1 + i * 0.1} floatIntensity={0.3}>
              {GeometryComponent && (
                <GeometryComponent
                  audioData={audioData}
                  isActive={true}
                  position={[0, 0, 0]}
                />
              )}
            </Float>

            {/* Chapter label on hover */}
            {isHovered && (
              <Text
                position={[0, -1.5, 0]}
                fontSize={0.3}
                color={chapter.color}
                anchorX="center"
                anchorY="middle"
                outlineWidth={0.02}
                outlineColor="#000000"
              >
                {chapter.title}
              </Text>
            )}
          </group>
        )
      })}
    </group>
  )
}

function Scene() {
  const groupRef = useRef()
  const orbitControlsRef = useRef()
  const { audioPlaying, audioData, currentChapter, setCurrentChapter } = useAppStore()

  // Convert audioData array to object format expected by components
  const audioDataObj = useMemo(() => {
    const fft = audioData || new Array(128).fill(0)
    const bass = fft.slice(0, 5).reduce((a, b) => a + b, 0) / 5
    const mids = fft.slice(5, 21).reduce((a, b) => a + b, 0) / 16
    const highs = fft.slice(21, 64).reduce((a, b) => a + b, 0) / 43
    const amplitude = fft.reduce((a, b) => a + b, 0) / fft.length

    return {
      fft,
      bass,
      mids,
      highs,
      amplitude,
      beat: amplitude > 0.5 // Simple beat detection
    }
  }, [audioData])

  return (
    <>
      {/* Audio Manager - handles audio playback and FFT analysis */}
      <AudioManager />

      {/* Smooth Camera Transitions - pass OrbitControls ref for coordination */}
      <CameraController orbitControlsRef={orbitControlsRef} />

      {/* Camera Controls - ref passed to CameraController for coordination */}
      <OrbitControls
        ref={orbitControlsRef}
        enablePan={false}
        enableZoom={true}
        minDistance={5}
        maxDistance={50}
        maxPolarAngle={Math.PI / 1.5}
        minPolarAngle={Math.PI / 3}
        enableDamping
        dampingFactor={0.05}
      />

      {/* Lighting */}
      <Lights />

      {/* Starfield background */}
      <Stars
        radius={100}
        depth={50}
        count={5000}
        factor={4}
        saturation={0}
        fade
        speed={0.5}
      />

      {/* Poem Display UI */}
      <PoemDisplay />

      {/* Main content */}
      <group ref={groupRef}>
        {currentChapter === null ? (
          // Overview mode - show all 14 chapters
          <ChapterOverview
            audioData={audioDataObj}
            onSelectChapter={setCurrentChapter}
          />
        ) : (
          // Single chapter mode with poem nodes
          <ChapterVisualization
            chapterIndex={currentChapter}
            isActive={true}
            audioData={audioDataObj}
          />
        )}
      </group>
    </>
  )
}

export default Scene
